import axios from "axios";
import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import logger from '../utils/logger';
import dayjs from 'dayjs';

const API_BASE_URL = "/api";
export const CLUB_ID = 186901;
const SEASON_START = '2025-07-01';

/**
 * Fonction de diagnostic pour tester la connexion à l'API FFF
 * Retourne un objet avec le statut et les détails du test
 */
export const testAPIConnection = async () => {
  const results = {
    apiAvailable: false,
    clubDataOK: false,
    equipesOK: false,
    classementOK: false,
    errors: [],
    warnings: []
  };

  try {
    // Test 1: Vérifier que l'API est accessible
    const clubRes = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}.json`, { timeout: 5000 });
    results.apiAvailable = true;
    results.clubDataOK = !!clubRes.data;

    // Test 2: Vérifier les équipes
    try {
      const equipesRes = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/equipes`, { timeout: 5000 });
      const equipes = equipesRes.data["hydra:member"];

      if (equipes && Array.isArray(equipes)) {
        results.equipesOK = true;
        results.warnings.push(`${equipes.length} équipe(s) trouvée(s)`);

        // Vérifier la structure des données
        if (equipes.length > 0 && !equipes[0].number) {
          results.warnings.push('ATTENTION: Structure de données modifiée - champ "number" manquant');
        }
      } else {
        results.errors.push('Format de données équipes invalide');
      }
    } catch (equipesError) {
      results.errors.push(`Erreur équipes: ${equipesError.message}`);
    }

    // Test 3: Vérifier le classement N2 Poule B (2025/2026)
    try {
      const classementRes = await axios.get(
        `${API_BASE_URL}/compets/439451/phases/1/poules/2/classement_journees`,
        { timeout: 5000 }
      );
      results.classementOK = !!(classementRes.data["hydra:member"] || Array.isArray(classementRes.data));
    } catch (classementError) {
      results.errors.push(`Erreur classement: ${classementError.message}`);
    }

  } catch (error) {
    results.errors.push(`API FFF inaccessible: ${error.message}`);
  }

  return results;
};

export const fetchClassement = async () => {
  try {
    // N2 Poule B (2025/2026) - cp_no: 439451, ph_no: 1, gp_no: 2
    const res = await axios.get(
      `${API_BASE_URL}/compets/439451/phases/1/poules/2/classement_journees`
    );
    const teams = res.data["hydra:member"] || res.data;

    if (!Array.isArray(teams)) {
      logger.error("Format de données classement invalide:", res.data);
      return [];
    }

    return teams.map(team => {
      const eq = team.equipe;
      // Recherche du logo du club (plusieurs fallback)
      const logo_path = eq?.logo || eq?.logoClub || eq?.logo_path || eq?.logo_url || (eq?.club && eq.club.logo) || null;
      return {
        rang: team.rank,
        nom: eq.short_name_federation || eq.short_name,
        points: team.point_count,
        joues: team.total_games_count,
        victoires: team.won_games_count,
        nuls: team.draw_games_count,
        defaites: team.lost_games_count,
        diff: team.goals_diff,
        logo: logo_path,
      };
    });
  } catch (error) {
    logger.error("Erreur lors de la récupération du classement:", error);
    return [];
  }
};

export const fetchStatsEquipeChampionnat = async ({ cpNo, phNo, gpNo, nomEquipe }) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/compets/${cpNo}/phases/${phNo}/poules/${gpNo}/classement_journees`);
    const journees = res.data["hydra:member"];

    if (!journees || !Array.isArray(journees) || journees.length === 0) {
      return null;
    }

    // Prendre la dernière journée
    const lastDate = journees.reduce((max, j) => (new Date(j.date) > new Date(max) ? j.date : max), journees[0].date);
    const lastJournee = journees.filter(j => j.date === lastDate);

    // Fonction utilitaire pour normaliser les noms
    const normalize = s => s?.normalize('NFD').replace(/[^\w\s-]/g, '').toLowerCase() || '';
    const nomEquipeNorm = normalize(nomEquipe);

    // Trouver l'objet correspondant à l'équipe
    return lastJournee.find(j => {
      const eq = j.equipe;
      const club = eq?.club;

      // Priorité 1 : Match par ID du club (plus fiable)
      if (club?.cl_no == CLUB_ID || club?.id == CLUB_ID) {
        return true;
      }

      // Priorité 2 : Match par nom (fallback)
      return [eq?.short_name_federation, eq?.short_name, eq?.nom, club?.short_name_federation, club?.short_name, club?.name]
        .map(normalize)
        .some(n => n.includes(nomEquipeNorm));
    });
  } catch (error) {
    logger.error('Erreur lors de la récupération des statistiques de l\'équipe:', error);
    return null;
  }
};

// Récupère le classement d'une compétition/phase/poule donnée
export const fetchClassementEquipe = async ({ cpNo, phNo, gpNo }) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/compets/${cpNo}/phases/${phNo}/poules/${gpNo}/classement_journees`);
    const journees = res.data["hydra:member"];
    if (!journees || journees.length === 0) return [];
    // Trouver la date la plus récente
    const lastDate = journees.reduce((max, j) => (new Date(j.date) > new Date(max) ? j.date : max), journees[0].date);
    // Filtrer toutes les journées de cette date (souvent une seule, mais sécurité)
    const lastJournees = journees.filter(j => j.date === lastDate);
    // Retourner tous les objets (un par équipe)
    return lastJournees;
  } catch (error) {
    logger.error('Erreur fetchClassementEquipe:', error);
    return [];
  }
};

// ... existing code ...
export const fetchEquipesChampionnat = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/equipes`);
    const equipes = res.data["hydra:member"];

    if (!equipes || !Array.isArray(equipes)) {
      logger.error('Structure de données API FFF invalide:', res.data);
      return [];
    }

    // On filtre les équipes avec au moins un engagement en championnat
    let equipesChamp = equipes
      .map((eq, index) => {
        // Vérifications de sécurité
        if (!eq || typeof eq !== 'object') return null;

        // Cherche le premier engagement de type CH (championnat)
        const engagements = eq.engagements || [];
        const engagementCH = engagements.find(e => e && e.competition && e.competition.type === "CH");
        if (!engagementCH) return null;

        // Extraire l'ID numérique de l'équipe
        // L'API a changé et ne renvoie plus @id, mais 'number' semble être l'identifiant unique (eq_no)
        let fffId = 'unknown';
        if (eq.number) {
          fffId = eq.number.toString();
        } else if (eq["@id"] && typeof eq["@id"] === 'string') {
          const parts = eq["@id"].split('/');
          fffId = parts[parts.length - 1] || `eq_${index}`;
        } else if (eq.id) {
          fffId = eq.id.toString();
        } else {
          fffId = `eq_${index}`;
        }

        return {
          id: eq["@id"] || eq.id || `eq_${index}`,
          fffId: fffId,
          nom: eq.category_label || eq.short_name || eq.category_code || 'Équipe sans nom',
          championnat: engagementCH.competition.name || 'Championnat inconnu',
          cp_no: engagementCH.competition?.cp_no,
          ph_no: engagementCH.phase?.number,
          gp_no: engagementCH.poule?.stage_number
        };
      })
      .filter(Boolean);

    // Définir un ordre de priorité pour les championnats
    const niveauOrder = [
      "N2", "National 2", "Seniors Régional 1", "U19", "U18", "U17", "U16", "U15", "U14", "U13", "U12", "U11", "U10"
    ];
    // Fonction pour trouver l'index du niveau dans la chaîne
    const getNiveauIndex = (championnat) => {
      const lower = championnat.toLowerCase();
      for (let i = 0; i < niveauOrder.length; i++) {
        if (lower.includes(niveauOrder[i].toLowerCase())) return i;
      }
      return 999; // tout ce qui n'est pas trouvé va à la fin
    };
    equipesChamp.sort((a, b) => getNiveauIndex(a.championnat) - getNiveauIndex(b.championnat));

    return equipesChamp;
  } catch (error) {
    logger.error("Erreur fetchEquipesChampionnat:", error);
    return [];
  }
};
// ... existing code ...

/**
 * Récupère le dernier résultat de match de l'équipe N2 Poule C
 * @returns {Promise<Object|null>} Le dernier match joué avec les scores
 */
export async function fetchDernierResultat() {
  try {
    const response = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/resultat`, { timeout: 10000 });
    const data = response.data;

    // Si c'est un tableau vide ou pas de hydra:member
    if (Array.isArray(data) && data.length === 0) {
      return null;
    }

    if (!data || !data["hydra:member"] || !Array.isArray(data["hydra:member"])) {
      return null;
    }

    // Ne garder que les matchs de l'équipe senior N2 (code 1, number 1, category SEM)
    // Accepter toutes les compétitions (N2, Coupe de France, etc.)
    const matchsSeniorN2 = data["hydra:member"].filter(
      (match) => {
        // On utilise == pour permettre la coercion de type (string/number)
        // IMPORTANT: On vérifie que c'est bien NOTRE club (CLUB_ID) qui est SEM 1
        const isHome = match?.home?.club?.cl_no == CLUB_ID && match?.home?.category_code === "SEM" && match?.home?.number == 1;
        const isAway = match?.away?.club?.cl_no == CLUB_ID && match?.away?.category_code === "SEM" && match?.away?.number == 1;
        const hasScore = match?.home_score !== null && match?.away_score !== null;

        return (isHome || isAway) && hasScore;
      }
    );

    if (matchsSeniorN2.length === 0) {
      return null;
    }

    // Trier du plus récent au plus ancien
    const matchsTries = matchsSeniorN2.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Retourner le plus récent, en ajoutant logo_path et short_name_federation
    const match = matchsTries[0];

    return {
      ...match,
      home: {
        ...match.home,
        short_name_federation: match.home?.short_name_federation || match.home?.nom || match.home?.name || 'Équipe domicile',
        logo_path: match.home?.club?.logo || match.home?.logo || null,
      },
      away: {
        ...match.away,
        short_name_federation: match.away?.short_name_federation || match.away?.nom || match.away?.name || 'Équipe extérieur',
        logo_path: match.away?.club?.logo || match.away?.logo || null,
      },
      terrain: {
        name: match.terrain?.name || match.terrain?.nom || 'Stade',
        city: match.terrain?.city || match.terrain?.ville || '',
      },
      competition: {
        name: match.competition?.name || 'N2',
      },
      poule: {
        name: match.poule?.name || '',
      },
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.error('Erreur fetchDernierResultat:', error);
    }
    return null;
  }
}

/**
 * Récupère tous les matchs de l'équipe N2 Poule C (passés et à venir)
 * @returns {Promise<{upcoming: Array, past: Array, all: Array}>} Les matchs triés
 */
export const fetchMatchs = async () => {
  try {
    // Stratégie hybride : on récupère le calendrier club ET les matchs de la poule N2
    // pour s'assurer d'avoir tous les matchs (certains manquent parfois dans le calendrier club)
    // Pour N2, on filtre à partir d'aujourd'hui pour être sûr d'avoir les matchs à venir
    const dateFilter = new Date();
    const dateStr = dateFilter.toISOString().split('T')[0];


    const [calendarRes] = await Promise.allSettled([
      axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/calendrier`, {
        timeout: 10000,
        params: { '_': Date.now() } // Cache buster
      }),
    ]);

    // 1. Traitement du calendrier club
    let clubMatchesRaw = [];
    if (calendarRes.status === 'fulfilled') {
      const data = calendarRes.value.data;
      if (Array.isArray(data)) {
        clubMatchesRaw = data;
      } else if (data && data["hydra:member"] && Array.isArray(data["hydra:member"])) {
        clubMatchesRaw = data["hydra:member"];
      } else {
        logger.warn('Format de données calendrier invalide:', data);
      }
    } else {
      logger.warn('Echec récupération calendrier club:', calendarRes.reason);
    }

    // Filtrage strict pour le calendrier (SEM 1 uniquement)
    const clubMatchesFiltered = clubMatchesRaw.filter(match => {
      const isHome = match?.home?.club?.cl_no == CLUB_ID && match?.home?.category_code === "SEM" && match?.home?.number == 1;
      const isAway = match?.away?.club?.cl_no == CLUB_ID && match?.away?.category_code === "SEM" && match?.away?.number == 1;
      return isHome || isAway;
    });

    // 2. Traitement des matchs N2 avec pagination (max 3 pages pour éviter trop de requêtes)
    let n2MatchesFiltered = [];
    try {
      let page = 1;
      let hasNextPage = true;
      const maxPages = 6;

      while (hasNextPage && page <= maxPages) {
        const res = await axios.get(`${API_BASE_URL}/compets/439451/phases/1/poules/2/matchs`, {
          timeout: 10000,
          params: {
            'date[after]': dateStr,
            'page': page,
            '_': Date.now()
          }
        });

        const data = res.data;
        const pageMatches = data["hydra:member"] || [];

        // Filter for our club immediately to save memory/processing
        const pageMatchesFiltered = pageMatches.filter(m =>
          m.home?.club?.cl_no == CLUB_ID || m.away?.club?.cl_no == CLUB_ID
        );

        n2MatchesFiltered = [...n2MatchesFiltered, ...pageMatchesFiltered];

        if (data["hydra:view"] && data["hydra:view"]["hydra:next"]) {
          page++;
        } else {
          hasNextPage = false;
        }
      }
    } catch (err) {
      logger.warn('Echec récupération matchs N2:', err);
    }

    // 3. Fusion et déduplication
    const allMatchesMap = new Map();

    // Priorité aux matchs N2 (souvent plus fiables pour le championnat)
    n2MatchesFiltered.forEach(m => {
      if (m.ma_no) allMatchesMap.set(m.ma_no, m);
    });

    // Ajout des matchs du calendrier s'ils ne sont pas déjà présents
    clubMatchesFiltered.forEach(m => {
      if (m.ma_no) {
        if (!allMatchesMap.has(m.ma_no)) {
          allMatchesMap.set(m.ma_no, m);
        }
      } else {
        // Fallback rare sans ma_no
        allMatchesMap.set(`temp_${Math.random()}`, m);
      }
    });

    const filteredMatches = Array.from(allMatchesMap.values());

    // On ajoute logo_path et normalise les noms d'équipes
    const mappedMatches = filteredMatches.map((match) => ({
      ...match,
      home: {
        ...match.home,
        short_name_federation: match.home?.short_name_federation || match.home?.short_name || match.home?.nom || match.home?.name || 'Équipe domicile',
        logo_path: match.home?.club?.logo || match.home?.logo || null,
      },
      away: {
        ...match.away,
        short_name_federation: match.away?.short_name_federation || match.away?.short_name || match.away?.nom || match.away?.name || 'Équipe extérieur',
        logo_path: match.away?.club?.logo || match.away?.logo || null,
      },
      terrain: {
        name: match.terrain?.name || match.terrain?.nom || 'Stade',
        city: match.terrain?.city || match.terrain?.ville || '',
      },
      competition: {
        name: match.competition?.name || 'N2',
      },
      poule: {
        name: match.poule?.name || 'POULE B',
      },
      match_membres: match.match_membres || [],
      terrain: {
        ...match.terrain,
        surface: match.terrain?.libelle_surface || '',
        address: match.terrain?.address || '',
        zip_code: match.terrain?.zip_code || '',
        city: match.terrain?.city || ''
      }
    }));

    // Fonction helper pour obtenir la date/heure complète du match
    const getMatchDateTime = (match) => {
      const matchDate = new Date(match.date);

      // Si le match a un champ `time` (format "18H00"), on l'ajoute à la date
      if (match.time && typeof match.time === 'string') {
        const timeMatch = match.time.match(/(\d{1,2})H(\d{2})/);
        if (timeMatch) {
          const hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);
          matchDate.setHours(hours, minutes, 0, 0);
        }
      }

      return matchDate;
    };

    // On sépare les matchs passés et à venir
    const now = new Date();
    const upcoming = mappedMatches
      .filter((m) => {
        const matchDateTime = getMatchDateTime(m);
        return matchDateTime > now;
      })
      .sort((a, b) => getMatchDateTime(a) - getMatchDateTime(b));

    const past = mappedMatches
      .filter((m) => {
        const matchDateTime = getMatchDateTime(m);
        const hasScore = m.home_score !== null && m.home_score !== undefined && m.away_score !== null && m.away_score !== undefined;
        const isPast = matchDateTime <= now;
        const isCurrentSeason = matchDateTime >= new Date(SEASON_START);

        return isPast && hasScore && isCurrentSeason;
      })
      .sort((a, b) => getMatchDateTime(b) - getMatchDateTime(a));

    return {
      upcoming,
      past,
      all: mappedMatches
    };
  } catch (error) {
    logger.error("Erreur fetchMatchs:", error);
    return { upcoming: [], past: [], all: [] };
  }
};



// Récupère l'évolution du classement d'une équipe sur la saison, avec date et adversaire, sur 30 journées
// N2 Poule B (2025/2026) par défaut
export const fetchClassementEvolution = async (equipeNom, cpNo = 439451, phNo = 1, gpNo = 2) => {
  try {
    const classementRes = await axios.get(
      `${API_BASE_URL}/compets/${cpNo}/phases/${phNo}/poules/${gpNo}/classement_journees`
    );
    const matchsRes = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/calendrier`);
    const allMatches = matchsRes.data["hydra:member"] || [];
    const filteredMatches = allMatches.filter(
      (match) =>
        match?.competition?.name === "N2" &&
        // Tous les matchs N2 (toutes poules confondues)
        (match.home?.nom?.toLowerCase().includes(equipeNom.toLowerCase()) || match.away?.nom?.toLowerCase().includes(equipeNom.toLowerCase()))
    );
    // On trie les journées par numéro croissant (pour éviter les trous)
    const journees = classementRes.data["hydra:member"]
      .map((j, idx) => ({ ...j, _idx: idx }))
      .sort((a, b) => (a.numero || a._idx) - (b.numero || b._idx));
    // On force à 30 journées (remplissage si besoin)
    const maxJournees = 30;
    let evo = [];
    for (let i = 0; i < maxJournees; i++) {
      const journee = journees[i] || {};
      const team = journee.classement ? journee.classement.find(
        t => t.equipe?.nom?.toLowerCase().includes(equipeNom.toLowerCase())
      ) : null;
      // Chercher le match correspondant à cette journée (par date la plus proche)
      let match = null;
      if (journee.date) {
        match = filteredMatches.find(m => m.date && m.date.slice(0, 10) === journee.date.slice(0, 10));
      }
      if (!match && filteredMatches[i]) match = filteredMatches[i];
      let adversaire = '';
      let domicile = '';
      if (match) {
        const isHome = match.home?.nom?.toLowerCase().includes(equipeNom.toLowerCase());
        adversaire = isHome ? match.away?.nom : match.home?.nom;
        domicile = isHome ? 'Domicile' : 'Extérieur';
      }
      evo.push({
        rang: team?.classement || null,
        journee: i + 1,
        date: journee.date ? journee.date.slice(0, 10) : '',
        adversaire: adversaire || '',
        domicile
      });
    }
    // Correction du dernier point : on prend le rang de la dernière journée réelle connue
    const lastKnown = evo.slice().reverse().find(j => j.rang !== null);
    if (lastKnown) {
      for (let i = evo.length - 1; i >= 0; i--) {
        if (evo[i].rang === null) evo[i].rang = lastKnown.rang;
      }
    }
    return evo;
  } catch (error) {
    logger.error("Erreur fetchClassementEvolution:", error);
    return [];
  }
};

/**
 * Récupère la série en cours (forme) pour une équipe
 * @param {string} equipeId - ID de l'équipe (facultatif, sinon utilise l'équipe première)
 * @returns {Promise<Array>} Tableau des 5 derniers résultats ['V', 'N', 'D', ...]
 */
export const fetchSerieCours = async (equipeId = null) => {
  try {
    // IDs des compétitions à suivre pour la forme (N2 et Coupe de France)
    const SEASON = 2025;
    // On récupère les IDs des compétitions (N2 et Coupe de France)
    const competitionIds = [439451, 435164];

    // On va chercher les matchs passés (ou jusqu'à demain pour être sûr d'avoir ceux du jour)
    // L'API semble ignorer le filtre competition.cp_no, donc on récupère "tout" le flux
    // et on filtre côté client. On récupère plusieurs pages pour être sûr de ne pas rater les matchs N2
    // noyés au milieu des matchs de jeunes.
    const dateBefore = dayjs().add(1, 'day').format('YYYY-MM-DD');
    const pagesToFetch = [1, 2, 3, 4, 5];

    const requests = pagesToFetch.map(page =>
      axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/matchs`, {
        params: {
          season: SEASON,
          'ma_dat[before]': dateBefore,
          page: page
        }
      })
    );

    const responses = await Promise.all(requests);

    // Fusionner tous les résultats
    let allMatches = [];
    responses.forEach(res => {
      if (res.data && res.data["hydra:member"]) {
        allMatches = allMatches.concat(res.data["hydra:member"]);
      }
    });

    // Filtrer : Compétition N2 ou Coupe, et Match Joué (score non null)
    const playedMatches = allMatches.filter(m => {
      const isN2OrCup = m.competition && competitionIds.includes(m.competition.cp_no);
      const hasScore = m.home_score !== null && m.away_score !== null;
      // On peut aussi vérifier que c'est bien l'équipe première (SEM 1) si le filtre compétition ne suffit pas
      // mais cp_no 439451 est spécifique N2.
      return isN2OrCup && hasScore;
    });

    // Trier par date décroissante (le plus récent en premier)
    playedMatches.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Prendre les 5 derniers
    const last5 = playedMatches.slice(0, 5);

    // Formater les résultats
    return last5.map(m => {
      const isHome = m.home.club.cl_no === CLUB_ID;
      const score = isHome
        ? `${m.home_score}-${m.away_score}`
        : `${m.away_score}-${m.home_score}`;

      let resultat = 'N';
      if (m.home_score === m.away_score) {
        resultat = 'N';
      } else if (isHome) {
        resultat = m.home_score > m.away_score ? 'V' : 'D';
      } else {
        resultat = m.away_score > m.home_score ? 'V' : 'D';
      }

      return {
        resultat: resultat,
        score: score,
        adversaire: isHome ? m.away.short_name : m.home.short_name,
        date: dayjs(m.date).format('DD/MM'),
        competition: m.competition.name
      };
    });

  } catch (error) {
    logger.error("Erreur fetchSerieCours:", error);
    return [];
  }
};

export const fetchEngagementsEquipe = async (equipeId) => {
  try {
    // Récupérer la liste des équipes du club
    const res = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/equipes`);
    const equipes = res.data["hydra:member"];

    if (!equipes || !Array.isArray(equipes)) {
      logger.error('Structure de données API FFF invalide pour engagements');
      return [];
    }

    // Trouver l'équipe correspondante au fffId avec protection
    const equipe = equipes.find(eq => {
      if (!eq) return false;
      const id = eq["@id"];
      if (!id || typeof id !== 'string') return false;
      const parts = id.split('/');
      return parts[parts.length - 1] === equipeId;
    });

    if (!equipe || !equipe.engagements || !Array.isArray(equipe.engagements)) {
      return [];
    }

    return equipe.engagements.map(engagement => ({
      id: engagement["@id"] || '',
      competition: engagement.competition ? {
        id: engagement.competition["@id"] || '',
        name: engagement.competition.name || '',
        type: engagement.competition.type || '',
        cp_no: engagement.competition.cp_no || ''
      } : {},
      phase: engagement.phase ? {
        id: engagement.phase["@id"] || '',
        number: engagement.phase.number || ''
      } : {},
      poule: engagement.poule ? {
        id: engagement.poule["@id"] || '',
        stage_number: engagement.poule.stage_number || ''
      } : {}
    }));
  } catch (error) {
    logger.error("Erreur fetchEngagementsEquipe:", error);
    return [];
  }
};

// Récupère le calendrier des matchs pour une équipe précise
export const fetchCalendrierEquipe = async (clubId, equipeId) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/clubs/${clubId}/equipes/${equipeId}/calendrier`, { timeout: 10000 });

    // L'API retourne maintenant directement un tableau (pas de wrapper hydra:member)
    let allMatches = [];
    if (Array.isArray(res.data)) {
      allMatches = res.data;
    } else if (res.data && res.data["hydra:member"] && Array.isArray(res.data["hydra:member"])) {
      // Fallback pour l'ancien format avec hydra:member
      allMatches = res.data["hydra:member"];
    } else {
      logger.warn(`Format de données calendrier invalide pour équipe ${equipeId}:`, res.data);
      return { upcoming: [], past: [], all: [] };
    }

    if (allMatches.length === 0) {
      logger.warn(`Aucun match trouvé pour équipe ${equipeId}`);
      return { upcoming: [], past: [], all: [] };
    }

    // On ajoute logo_path et normalise les noms d'équipes
    const mappedMatches = allMatches.map((match) => ({
      ...match,
      home: {
        ...match.home,
        short_name_federation: match.home?.short_name_federation || match.home?.short_name || match.home?.nom || match.home?.name || 'Équipe domicile',
        logo_path: match.home?.club?.logo || match.home?.logo || null,
      },
      away: {
        ...match.away,
        short_name_federation: match.away?.short_name_federation || match.away?.short_name || match.away?.nom || match.away?.name || 'Équipe extérieur',
        logo_path: match.away?.club?.logo || match.away?.logo || null,
      },
      terrain: {
        name: match.terrain?.name || match.terrain?.nom || 'Stade',
        city: match.terrain?.city || match.terrain?.ville || '',
      },
      competition: {
        name: match.competition?.name || '',
      },
      poule: {
        name: match.poule?.name || '',
      },
    }));

    // Fonction helper pour obtenir la date/heure complète du match
    const getMatchDateTime = (match) => {
      const matchDate = new Date(match.date);

      // Si le match a un champ `time` (format "18H00"), on l'ajoute à la date
      if (match.time && typeof match.time === 'string') {
        const timeMatch = match.time.match(/(\d{1,2})H(\d{2})/);
        if (timeMatch) {
          const hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);
          matchDate.setHours(hours, minutes, 0, 0);
        }
      }

      return matchDate;
    };

    // On sépare les matchs passés et à venir
    const now = new Date();
    const upcoming = mappedMatches
      .filter((m) => {
        const matchDateTime = getMatchDateTime(m);
        return matchDateTime > now;
      })
      .sort((a, b) => getMatchDateTime(a) - getMatchDateTime(b));

    const past = mappedMatches
      .filter((m) => {
        const matchDateTime = getMatchDateTime(m);
        const hasScore = m.home_score !== null && m.home_score !== undefined && m.away_score !== null && m.away_score !== undefined;
        const isPast = matchDateTime <= now;
        const isCurrentSeason = matchDateTime >= new Date(SEASON_START);

        return isPast && hasScore && isCurrentSeason;
      })
      .sort((a, b) => getMatchDateTime(b) - getMatchDateTime(a));

    return { upcoming, past, all: mappedMatches };
  } catch (error) {
    logger.error(`Erreur fetchCalendrierEquipe pour équipe ${equipeId}:`, error.message);
    return { upcoming: [], past: [], all: [] };
  }
};

// Récupère le staff (éducateurs ou référent) pour une équipe donnée
export const fetchStaffEquipe = async (clubId, equipeNom) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/clubs/${clubId}.json`);
    const membres = res.data.membres || [];
    const nomEquipe = (equipeNom || '').toLowerCase();
    // Détection équipe féminine
    const isFeminine = nomEquipe.includes('fem') || nomEquipe.includes('fémin');
    // Détection catégorie (ex: 'u18', 'n2', 'seniors', etc.)
    const catMatch = nomEquipe.match(/u\d+/i);
    const cat = catMatch ? catMatch[0] : (nomEquipe.includes('n2') ? 'n2' : (nomEquipe.includes('seniors') ? 'seniors' : ''));
    // Filtrage strict : on ne garde que les membres dont la catégorie ou le nom court correspond exactement à l'équipe
    const staff = membres.filter(m => {
      const lib = (m.ti_lib || '').toLowerCase();
      // Pour les équipes féminines, on prend le référent féminin uniquement si la catégorie correspond
      if (isFeminine && lib.includes('referent feminin')) {
        if (cat && lib.includes(cat)) return true;
        if (!cat && (lib.includes('fem') || lib.includes('fémin'))) return true;
        return false;
      }
      // On cherche la catégorie ou le nom court dans le libellé (ex: 'u18', 'n2', etc.)
      if (cat && !lib.includes(cat)) return false;
      if (!cat && nomEquipe && !lib.includes(nomEquipe)) return false;
      // On garde les éducateurs ou responsables techniques de la catégorie
      return lib.includes("educateur d'equipe") || lib.includes('responsable technique categorie');
    });
    // On retourne nom, prénom, fonction
    return staff.map(m => ({
      nom: m.in_nom,
      prenom: m.in_prenom,
      fonction: m.ti_lib
    }));
  } catch (error) {
    logger.error('Erreur fetchStaffEquipe:', error);
    return [];
  }
};

// Récupère TOUS les matchs du club pour la saison en cours (passés et à venir)
// Gère la pagination automatiquement
export const fetchAllClubMatches = async () => {
  try {
    const dateAfter = SEASON_START; // '2025-07-01'

    // Première requête pour avoir le nombre total de pages
    const firstRes = await axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/matchs`, {
      params: {
        'ma_dat[after]': dateAfter,
        page: 1
      }
    });

    if (!firstRes.data || !firstRes.data["hydra:member"]) {
      return [];
    }

    const totalItems = firstRes.data["hydra:totalItems"] || 0;
    const itemsPerPage = firstRes.data["hydra:member"].length || 30;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    let allMatches = [...firstRes.data["hydra:member"]];

    if (totalPages > 1) {
      // Créer les promesses pour les pages suivantes
      const promises = [];
      for (let page = 2; page <= totalPages; page++) {
        promises.push(
          axios.get(`${API_BASE_URL}/clubs/${CLUB_ID}/matchs`, {
            params: {
              'ma_dat[after]': dateAfter,
              page: page
            }
          })
        );
      }

      // Exécuter en parallèle
      const responses = await Promise.all(promises);

      responses.forEach(res => {
        if (res.data && res.data["hydra:member"]) {
          allMatches = allMatches.concat(res.data["hydra:member"]);
        }
      });
    }

    // Normalisation des données
    return allMatches.map(match => ({
      ...match,
      home: {
        ...match.home,
        short_name_federation: match.home?.short_name_federation || match.home?.short_name || match.home?.nom || match.home?.name || 'Équipe domicile',
        logo_path: match.home?.club?.logo || match.home?.logo || null,
      },
      away: {
        ...match.away,
        short_name_federation: match.away?.short_name_federation || match.away?.short_name || match.away?.nom || match.away?.name || 'Équipe extérieur',
        logo_path: match.away?.club?.logo || match.away?.logo || null,
      },
      terrain: {
        name: match.terrain?.name || match.terrain?.nom || 'Stade',
        city: match.terrain?.city || match.terrain?.ville || '',
      },
      competition: {
        name: match.competition?.name || '',
      },
      poule: {
        name: match.poule?.name || '',
      },
    }));

  } catch (error) {
    logger.error("Erreur fetchAllClubMatches:", error);
    return [];
  }
};

// Récupère le staff Firestore pour une équipe donnée
export const fetchStaffFirestore = async (equipeId) => {
  const q = query(collection(db, 'staff'), where('equipe', '==', equipeId));
  const snap = await getDocs(q);
  return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Ajoute un membre du staff Firestore
export const addStaffFirestore = async (staff) => {
  const docRef = await addDoc(collection(db, 'staff'), staff);
  return { id: docRef.id, ...staff };
};

// Modifie un membre du staff Firestore
export const updateStaffFirestore = async (id, data) => {
  await updateDoc(doc(collection(db, 'staff'), id), data);
};

// Supprime un membre du staff Firestore
export const deleteStaffFirestore = async (id) => {
  await deleteDoc(doc(collection(db, 'staff'), id));
};
